import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Chip, IconButton, Typography } from '@mui/material';

import { useMemo, useState } from 'react';
import { Stripe } from 'stripe';
import api from '~/api/Endpoints';
import { useQuery } from '~/hooks/useQuery';
import { useUser } from '~/hooks/useUser';
import { useHandleException } from '~/scripts/handleException';
export interface GetAppAccountCustomers {
  id: string;
  sources: Stripe.ApiList<Stripe.CustomerSource> | undefined;
  subscriptions: Stripe.ApiList<Stripe.Subscription> | undefined;
  default_source: string | undefined;
}

export const ApiUsageCard = () => {
  const user = useUser();
  const handleException = useHandleException();
  const [refreshCount, setRefreshCount] = useState(0);
  const getApiUsageQuery = useQuery({
    queryKey: [user?.uuid, refreshCount],
    queryFn: async () => {
      return api.get(`/api-access/usage`, {
        headers: {
          'Cache-Control': 'no-cache',
        },
      });
    },
    onError(error) {
      handleException(error as Error, { category: 'api-usage' });
    },
  });

  const getAppAccountCustomersQuery = useQuery({
    queryKey: [user?.uuid, refreshCount],
    queryFn: async () => {
      return api.get(`/app/account/customers`);
    },
    onError(error) {
      handleException(error as Error, { category: 'api-usage-customer' });
    },
  });

  const billingPeriod = useMemo(() => {
    const subscription =
      getAppAccountCustomersQuery.data?.subscriptions?.data.find(
        (subscription) => {
          return !subscription.plan;
        }
      );
    if (!subscription) {
      return '';
    }
    return `${new Date(
      subscription.current_period_start * 1000
    ).toLocaleDateString()} to ${new Date(
      subscription.current_period_end * 1000
    ).toLocaleDateString()}`;
  }, [getAppAccountCustomersQuery.data]);

  const UsageLabel = ({
    endpoint,
    usage,
  }: {
    endpoint: string;
    usage: number;
  }) => {
    return (
      <>
        <Typography
          component="span"
          variant="body1"
          sx={{ fontFamily: 'courier', mr: 2 }}>
          {endpoint}
        </Typography>
        <Typography component="span" variant="body1">
          {usage.toLocaleString()}
        </Typography>
      </>
    );
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Current Month&apos;s Usage
        </Typography>
        <IconButton onClick={() => setRefreshCount((count) => count + 1)}>
          <RefreshIcon />
        </IconButton>
      </Box>
      <Typography>{billingPeriod}</Typography>
      <br />
      {getApiUsageQuery.data?.map(([usage], index) => {
        if (!usage) {
          return null;
        }
        return (
          <Chip
            key={index}
            variant="outlined"
            sx={{ mr: 1 }}
            label={
              <UsageLabel
                endpoint={['/search', '/domains', '/downloads'][index] || ''}
                usage={usage?.total_usage || 0}
              />
            }
            color={usage?.total_usage > 0 ? 'success' : 'info'}
          />
        );
      })}
    </>
  );
};
